import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Avatar,
  Chip,
  Rating,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Stack,
  Button,
  Tooltip
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Star as StarIcon,
  Bookmark as BookmarkIcon,
  Share as ShareIcon,
  Comment as CommentIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  People as PeopleIcon,
  VisibilityOff as VisibilityOffIcon
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import axios from "axios";
import { API_URL, popupTimer } from "../../consts";
import { toggleLike } from "../../services/likeService";

// Components
import ChipList from "../common/ChipList";
import PublisherComment from "./PublisherComment";
import StatisticsDialog from "../common/StatisticsDialog";
import HomeCardActionsMenu from "./HomeCardActionsMenu";
import AddToSpace from "./AddToSpace";
import HomeCardDialog from "./HomeCardDialog";
import RatingObject from "../RatingObject/RatingObject";
import CommentsDialog from "./CommentsDialog";
import PublicVisibilityDialog from "../common/PublicVisibilityDialog";

const FeedCardItem = ({
  data,
  searhTags,
  selected,
  isSelected,
  displayStars = true,
  isBookmark = false,
  isFeedback = false,
  applyMargin = false,
  onRemoveFromSpaceClick,
  onAddToSpaceClick,
  onRemove,
  onDelete, // Add delete handler prop
  onRateSuccessfully,
  onSelect,
  displayRemoveFromSpace = false,
  displayAddToSpace = false,
  showRemove = false,
  showSpacePopup: externalShowSpacePopup,
  enableDragAndDrop, // New prop to enable drag and drop
  currentSpaceId, // Add current space context
  parentSpaceId, // Add parent space ID
  isCommunity = false, // Add isCommunity prop
  onVisibilityUpdate, // Callback when visibility is updated
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authorized = useSelector((state) => state.authorized);
  const currentUser = useSelector((state) => state.user);

  // State
  const [showRateModal, setShowRateModal] = useState(false);
  const [openStatsModal, setOpenStatsModal] = useState(false);
  const [rateId, setRateId] = useState(null);
  const [openSpaceModal, setOpenSpaceModal] = useState(false);
  const [showRatingObject, setShowRatingObject] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openedItemData, setDetailedDataDialog] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(data.likes_count || data.likes || 0);
  const [commentsCount, setCommentsCount] = useState(data.comments_count || 0);
  const [isDragging, setIsDragging] = useState(false);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);
  const [localData, setLocalData] = useState(data);
  const openMenu = Boolean(anchorEl);

  // Sync localData with data prop when it changes
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  // Sync like count when data changes
  useEffect(() => {
    setLikesCount(localData.likes_count || localData.likes || 0);
  }, [localData.likes_count, localData.likes]);

  // Handlers
  const handleMenuClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRatingObjectClose = () => {
    setShowRatingObject(false);
  };

  const handleAddToSpaceClick = () => {
    setRateId(localData.id);
    setShowRatingObject(true);
  };

  const handleCardClick = (e) => {
    if (!openSpaceModal && !isDragging) {
      setDetailedDataDialog(localData);
    }
  };

  // Handle data update from dialog (e.g., when image is uploaded)
  const handleDataUpdate = (updatedData) => {
    // Update local data state to reflect changes
    setLocalData(updatedData);
    // Also update the opened dialog data if dialog is open
    if (openedItemData) {
      setDetailedDataDialog(updatedData);
    }
  };

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authorized || !localData.rating_id) return;
    
    setIsLiking(true);
    try {
      const response = await toggleLike({ rating_id: data.rating_id });
      setLikesCount(response.like_count || 0);
      setIsLiked(!isLiked);
    } catch (error) {
      if (error.message === "UNAUTHORIZED") {
        dispatch({ type: "SET_UNAUTHORIZED" });
      } else {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: error?.response?.data?.error || "Failed to like. Please try again.",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      }
    } finally {
      setIsLiking(false);
    }
  };

  const handleComment = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCommentsDialog(true);
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Share functionality
    if (navigator.share) {
      navigator.share({
        title: data.name,
        text: data.description,
        url: window.location.href,
      });
    }
  };

  const showSpacePopup = (objectId, itemData = null) => {
    if (externalShowSpacePopup) {
      externalShowSpacePopup(objectId, itemData || data);
    } else {
      if (authorized) {
        setRateId(objectId);
        setOpenSpaceModal(true);
      } else {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "You must Login to Perform this Action",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({
            type: "SET_POPUP",
            payload: { text: "", type: "" },
          });
        }, popupTimer);
      }
    }
  };

  const handleMenuAction = (action, data) => {
    if (action === 'spaceClick') {
      axios.get(`${API_URL}/rates/ratings/${data.rating_id}`)
        .then(({ data: responseData }) => {
          setDetailedDataDialog(responseData);
        })
        .catch((error) => {
          console.error("Error fetching space data:", error);
          dispatch({
            type: "SET_POPUP",
            payload: {
              text: "Error loading space data",
              type: "error",
            },
          });
        });
    }
  };

  const onRatingClick = () => {
    setOpenStatsModal(true);
  };

  const getDomain = (url) => {
    try {
      if (!url) return '';
      const parsedUrl = new URL(url);
      return parsedUrl.hostname;
    } catch (error) {
      return '';
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e) => {
    if (!enableDragAndDrop) return;
    
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "copy";
    
    // Store the rated object data for the drop handler
    const dragData = {
      type: "rated-object",
      ratedObject: {
        id: localData.id, // This is the rating ID
        title: localData.title || localData.name,
        comment: localData.comment || localData.description,
        rate_points: localData.rate_points || localData.average_stars,
        tags: localData.tags || localData.keywords || [],
        image: localData.image || localData.image_url || "",
        rated_object_id: localData.rated_object_id || localData.id, // This is the actual object ID
        object_id: localData.object_id || localData.rated_object_id || localData.id, // Ensure we have object_id
        linkUrl: localData.metadata?.page_url || "",
        // Include all data needed for AddToSpace dialog
        fullData: localData
      }
    };
    
    e.dataTransfer.setData("application/json", JSON.stringify(dragData));
    
    // Create a custom drag image
    const dragGhost = document.createElement("div");
    dragGhost.textContent = `📄 ${localData.title || localData.name || 'Rated Object'}`;
    dragGhost.style.position = "absolute";
    dragGhost.style.top = "-9999px";
    dragGhost.style.left = "-9999px";
    dragGhost.style.fontSize = "14px";
    dragGhost.style.fontWeight = "bold";
    dragGhost.style.padding = "8px 12px";
    dragGhost.style.borderRadius = "6px";
    dragGhost.style.backgroundColor = "#fff";
    dragGhost.style.border = "2px solid #2196F3";
    dragGhost.style.boxShadow = "0px 4px 12px rgba(33, 150, 243, 0.3)";
    dragGhost.style.color = "#2196F3";
    dragGhost.style.maxWidth = "300px";
    dragGhost.style.whiteSpace = "nowrap";
    dragGhost.style.overflow = "hidden";
    dragGhost.style.textOverflow = "ellipsis";

    document.body.appendChild(dragGhost);
    e.dataTransfer.setDragImage(dragGhost, 0, 0);

    setTimeout(() => {
      document.body.removeChild(dragGhost);
    }, 0);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleVisibilityClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setVisibilityDialogOpen(true);
  };

  const handleVisibilityUpdate = (newIsPublic) => {
    // Update local data if needed
    // The parent component should refresh the data
    if (onVisibilityUpdate) {
      onVisibilityUpdate(newIsPublic);
    }
  };

  return (
    <>
      <div
        draggable={enableDragAndDrop}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        style={{
          cursor: enableDragAndDrop ? 'grab' : 'default',
          opacity: isDragging ? 0.7 : 1,
          transition: 'opacity 0.2s ease',
          width: '100%',
          height: '100%',
        }}
      >
        <Card
          sx={{
            border: selected || isSelected ? "1px solid #128d6f" : "1px solid #efefef",
            borderRadius: 0,
            boxShadow: "none",
            cursor: "pointer",
            mb: 0,
            backgroundColor: '#fff',
            width: "100%",
            borderTop: "1px solid #efefef",
            borderBottom: "1px solid #efefef",
            "&:first-of-type": {
              borderTop: "none"
            },
            "&:hover": {
              borderColor: "#efefef",
              transition: "border-color 0.2s ease"
            }
          }}
          onClick={handleCardClick}
        >
          {/* Header Section */}
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  alt={localData.display_name || (localData.first_name && localData.last_name ? `${localData.first_name} ${localData.last_name}` : localData.first_name || localData.last_name || localData.user_name || localData.user_display_name || "User")}
                  src={localData.profile_image_url}
                  sx={{ width: 32, height: 32 }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px', lineHeight: 1.2 }}>
                    {localData.display_name || (localData.first_name && localData.last_name ? `${localData.first_name} ${localData.last_name}` : localData.first_name || localData.last_name || localData.user_name || localData.user_display_name || "Anonymous User")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                    {new Date(localData.create_time || Date.now()).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isBookmark && displayStars && localData.average_stars > 0 && (
                  <Rating
                    value={localData.average_stars}
                    readOnly
                    size="small"
                    emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                    sx={{ color: "rgba(255, 204, 72, 1)", fontSize: '16px' }}
                  />
                )}
                {!isCommunity && localData.is_public && (
                  <Tooltip title="Click to change visibility" arrow>
                    <IconButton
                      size="small"
                      onClick={handleVisibilityClick}
                      sx={{
                        color: "#6366f1",
                        p: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(99, 102, 241, 0.1)",
                          color: "#6366f1",
                        },
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Tooltip>
                )}
                
                {!isCommunity && !localData.is_public && (
                  <Tooltip title="Click to change visibility" arrow>
                    <IconButton
                      size="small"
                      onClick={handleVisibilityClick}
                      sx={{
                        color: "#6366f1",
                        p: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(99, 102, 241, 0.1)",
                          color: "#6366f1",
                        },
                      }}
                    >
                      <VisibilityOffIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  onClick={handleMenuClick}
                  sx={{ color: 'text.secondary', p: 0.5 }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Title */}
            {localData.name && (
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, fontSize: '15px' }}>
                {localData.name}
              </Typography>
            )}

            {/* Description */}
            {localData.description && (
              <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4, fontSize: '14px' }}>
                <PublisherComment comment={localData.description} onClickDisabled={true} />
              </Typography>
            )}

            {/* Image */}
            {localData.image_url && (
              <Box sx={{ 
                mb: 1.5, 
                borderRadius: 0, 
                overflow: 'hidden',
                marginLeft: { xs: -1.5, sm: -2 },
                marginRight: { xs: -1.5, sm: -2 },
                width: { xs: 'calc(100% + 24px)', sm: 'calc(100% + 32px)' },
                backgroundColor: '#000'
              }}>
                <img
                  src={localData.image_url}
                  alt={localData.name || "Review image"}
                  style={{
                    width: '100%',
                    maxHeight: '600px',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block'
                  }}
                />
              </Box>
            )}

            {/* Caption/Content - Instagram style */}
            {(localData.content || localData.title) && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ 
                  fontSize: '14px', 
                  lineHeight: 1.4,
                  color: '#262626'
                }}>
                  {localData.content || localData.title}
                </Typography>
              </Box>
            )}

            {/* Tags */}
            {((localData.keywords && localData.keywords.length > 0) || (localData.tags && localData.tags.length > 0)) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ 
                  color: '#8e8e8e', 
                  fontSize: '11px', 
                  fontWeight: 500, 
                  mb: 0.5, 
                  display: 'block' 
                }}>
                  TAGS
                </Typography>
                <ChipList 
                  list={localData.keywords || localData.tags || []} 
                  highlightList={searhTags} 
                  AllowChipOverflow={true} 
                />
              </Box>
            )}

            {/* Collections/Spaces List */}
            {localData.spaces_list && localData.spaces_list.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ 
                  color: '#8e8e8e', 
                  fontSize: '11px', 
                  fontWeight: 500, 
                  mb: 0.5, 
                  display: 'block' 
                }}>
                  COLLECTIONS
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ flexWrap: 'wrap', gap: 0.5 }}
                >
                  {localData.spaces_list.map((space) => (
                    <Chip
                      key={space.id}
                      label={space.name}
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleMenuAction('spaceClick', space);
                      }}
                      sx={{
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        color: 'primary.main',
                        fontSize: '12px',
                        height: '24px',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.15)',
                        },
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </CardContent>

          {/* Actions Section */}
          <CardActions sx={{ px: { xs: 1.5, sm: 2 }, py: 1, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={handleLike}
                  disabled={isLiking || !authorized}
                  sx={{ 
                    color: isLiked ? '#e91e63' : '#262626', 
                    p: 0.5, 
                    '&:hover': { 
                      color: isLiked ? '#c2185b' : '#8e8e8e', 
                      backgroundColor: 'transparent' 
                    } 
                  }}
                >
                  {isLiked ? (
                    <FavoriteIcon fontSize="small" />
                  ) : (
                    <FavoriteBorderIcon fontSize="small" />
                  )}
                </IconButton>
                {likesCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', ml: 0.5 }}>
                    {likesCount}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={handleComment}
                  sx={{ color: '#262626', p: 0.5, '&:hover': { color: '#8e8e8e', backgroundColor: 'transparent' } }}
                >
                  <CommentIcon fontSize="small" />
                </IconButton>
                {commentsCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', ml: 0.5 }}>
                    {commentsCount}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isFeedback && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: "#dbdbdb",
                    color: "#262626",
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: '12px',
                    px: 2,
                    py: 0.5,
                    minHeight: '28px',
                    "&:hover": {
                      backgroundColor: "#fafafa",
                      borderColor: "#c7c7c7",
                    }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Handle feedback action
                  }}
                >
                  Reply
                </Button>
              )}
            </Box>
          </CardActions>

          {/* Menu Popup */}
          <HomeCardActionsMenu
            anchorEl={anchorEl}
            openMenu={openMenu}
            handleClose={handleMenuClose}
            data={data}
            showSpacePopup={showSpacePopup}
            displayRemoveFromSpace={displayRemoveFromSpace}
            onRemoveFromSpaceClick={onRemoveFromSpaceClick}
            displayAddToSpace={displayAddToSpace}
            onAddToSpaceClick={handleAddToSpaceClick}
            showRemove={showRemove}
            onRemove={onRemove}
            onDelete={onDelete}
            isFeedback={isFeedback}
            currentUser={currentUser}
          />

          {/* Statistics Dialog */}
          <StatisticsDialog
            data={data?.stars}
            total={data?.stars && Object.values(data.stars).reduce((sum, a) => sum + a, 0)}
            handleClose={() => setOpenStatsModal(false)}
            open={openStatsModal}
          />

          {/* Add to Space Dialog */}
          {openSpaceModal && (
            <AddToSpace
              open={openSpaceModal}
              rateId={rateId}
              objectId={localData.id}
              itemData={localData}
              currentSpaceId={currentSpaceId}
              handleClose={() => setOpenSpaceModal(false)}
              authorized={authorized}
            />
          )}

          {/* Home Card Dialog */}
          {openedItemData && (
            <HomeCardDialog
              open={openedItemData}
              data={openedItemData}
              onReadMore={() => {/* Handle read more */}}
              onUpdate={handleDataUpdate}
              onClose={(e) => {
                e.stopPropagation();
                setDetailedDataDialog(null);
              }}
            />
          )}

          {/* Rating Object Dialog for Add to Space */}
          <RatingObject
            open={showRatingObject}
            handleClose={handleRatingObjectClose}
            id={localData.id}
            initialData={localData}
            preselectedSpaceId={currentSpaceId}
            preselectedParentSpaceId={parentSpaceId}
            onRateSuccessfully={(id) => {
              setShowRatingObject(false);
              if (onRateSuccessfully) {
                onRateSuccessfully(id);
              }
            }}
          />

          {/* Comments Dialog */}
          <CommentsDialog
            open={showCommentsDialog}
            onClose={() => setShowCommentsDialog(false)}
            rateId={localData.id}
          />

          {/* Public Visibility Dialog */}
          {visibilityDialogOpen && (
            <PublicVisibilityDialog
              open={visibilityDialogOpen}
              onClose={() => setVisibilityDialogOpen(false)}
              ratingId={localData.rating_id || localData.id}
              currentIsPublic={localData.is_public}
              onUpdate={handleVisibilityUpdate}
            />
          )}
        </Card>
      </div>
    </>
  );
};

export default FeedCardItem; 